/**
 * Settings information architecture (Figma 09 · Settings and connectors).
 * Three scopes: You, the open workspace, the open brand. Notifications and
 * Approvals are drawn in the design but have no backend yet, so they are not
 * listed (see creative-os-backend/docs/settings-figma-plan.md §6).
 */
import type { OrganizationRole } from "@/types/organization.type";

export const SETTINGS_PAGES = [
  "profile",
  "workspaces",
  "personal-memory",
  "appearance",
  "your-usage",
  "connected-apps",
  "general",
  "people",
  "accounts",
  "brands",
  "workspace-usage",
  "plan",
  "social-accounts",
  "clients",
  "brand-memory",
  "calendar",
  "your-access",
] as const;

export type SettingsPageId = (typeof SETTINGS_PAGES)[number];

export type SettingsScope = "you" | "workspace" | "brand";

export type SettingsNavItem = {
  id: SettingsPageId;
  label: string;
  /** One line under the label on the compact "All settings" index. */
  hint: string;
  /** Roles that may open the page; omitted = every role. */
  roles?: OrganizationRole[];
};

export type SettingsNavSection = {
  scope: SettingsScope;
  title: string;
  items: SettingsNavItem[];
};

const TEAM: OrganizationRole[] = ["Owner", "Contributor", "Viewer"];

export const SETTINGS_NAV: SettingsNavSection[] = [
  {
    scope: "you",
    title: "You",
    items: [
      { id: "profile", label: "Profile and sign-in", hint: "Name and password" },
      { id: "workspaces", label: "Workspaces", hint: "The workspaces you belong to" },
      { id: "personal-memory", label: "Personal memory", hint: "What the agents remember about you", roles: TEAM },
      { id: "appearance", label: "Appearance", hint: "Theme, motion, surfaces" },
      { id: "your-usage", label: "Your usage", hint: "Your monthly AI allowance" },
      { id: "connected-apps", label: "Connected apps", hint: "Claude and other apps, API keys", roles: TEAM },
    ],
  },
  {
    scope: "workspace",
    title: "Workspace",
    items: [
      // A client's own page, where the team's General would be.
      { id: "your-access", label: "Your access", hint: "What you can see and do here", roles: ["Guest"] },
      { id: "general", label: "General", hint: "Name and owner", roles: TEAM },
      { id: "people", label: "People", hint: "Team and invitations", roles: TEAM },
      { id: "accounts", label: "Accounts", hint: "Every brand's social accounts", roles: TEAM },
      { id: "brands", label: "Brands", hint: "Rename, logo, delete", roles: TEAM },
      { id: "workspace-usage", label: "Usage", hint: "The pool and each person's allowance", roles: ["Owner"] },
      { id: "plan", label: "Plan and seats", hint: "Plan, seats, budgets", roles: TEAM },
    ],
  },
  {
    scope: "brand",
    title: "Brand",
    items: [
      { id: "social-accounts", label: "Social accounts", hint: "The brand's connected accounts" },
      { id: "clients", label: "Clients", hint: "Who outside the team can see the brand", roles: ["Owner", "Contributor"] },
      { id: "brand-memory", label: "Brand memory", hint: "What the agents remember about the brand", roles: ["Owner"] },
      { id: "calendar", label: "Calendar", hint: "Posts a month, planning, posting times", roles: TEAM },
    ],
  },
];

/**
 * A guest's Settings (Figma 09 · The client's Settings): the same layout as
 * the team's, with only these pages in it; "Your access" opens first. Guests also connect their brand's social accounts and
 * have a usage meter today, so those stay reachable from the hub, and switch
 * workspace from Workspaces like anyone else.
 */
export const GUEST_PAGES: ReadonlySet<SettingsPageId> = new Set<SettingsPageId>([
  "your-access",
  "profile",
  "workspaces",
  "appearance",
  "your-usage",
  "social-accounts",
]);

export const GUEST_DEFAULT_PAGE: SettingsPageId = "your-access";

/** Pages that belong to the person, open without any workspace. */
export const NO_ORG_PAGES: ReadonlySet<SettingsPageId> = new Set<SettingsPageId>([
  "profile",
  "workspaces",
  "appearance",
  "your-usage",
]);

/** Old ?page= / ?tab= values. `connections` must stay: the backend's social
 *  OAuth callbacks redirect to ?page=connections&connected=… */
const LEGACY_PAGE_MAP: Record<string, SettingsPageId> = {
  connections: "social-accounts",
  members: "people",
  "team-invites": "people",
  invitations: "workspaces",
  password: "profile",
  integrations: "connected-apps",
  "api-keys": "connected-apps",
  usage: "your-usage",
  "org-usage": "workspace-usage",
  memory: "brand-memory",
  organization: "general",
  brand: "general",
};

export function resolveSettingsPage(value: string | null): SettingsPageId | null {
  if (!value) return null;
  if (SETTINGS_PAGES.includes(value as SettingsPageId)) return value as SettingsPageId;
  return LEGACY_PAGE_MAP[value] ?? null;
}

export function navItemFor(page: SettingsPageId): SettingsNavItem | undefined {
  for (const section of SETTINGS_NAV) {
    const item = section.items.find((i) => i.id === page);
    if (item) return item;
  }
  return undefined;
}

export function scopeOf(page: SettingsPageId): SettingsScope {
  return SETTINGS_NAV.find((s) => s.items.some((i) => i.id === page))?.scope ?? "you";
}

/** Whether a role may open a page. `role` null = no workspace at all. */
export function canOpenPage(page: SettingsPageId, role: OrganizationRole | null): boolean {
  if (role === null) return NO_ORG_PAGES.has(page);
  if (role === "Guest") return GUEST_PAGES.has(page);
  if (page === "your-access") return false;
  const item = navItemFor(page);
  return !item?.roles || item.roles.includes(role);
}

export function defaultPageFor(role: OrganizationRole | null): SettingsPageId {
  if (role === null) return "workspaces";
  if (role === "Guest") return GUEST_DEFAULT_PAGE;
  return "profile";
}

export function settingsHref(page: SettingsPageId, extra?: Record<string, string>): string {
  const params = new URLSearchParams({ page, ...extra });
  return `/dashboard/settings?${params.toString()}`;
}
